import { Button, FAIcon } from '@/components'
import { useMutation } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { differenceInDays, parseISO } from 'date-fns'
import type { ResultOf } from 'gql.tada'
import { useState } from 'react'
import { NEW_DEMO_DAYS } from '../consts'
import { useDemoViewTracking } from '../hooks'
import { SET_LEARNING_DEMO_COMPLETED_MUTATION } from '../mutations'
import type { LEARNING_FOLDERS_QUERY } from '../queries'
import { StorylaneEmbed } from './storylane-embed'

type LearningFolder = ResultOf<typeof LEARNING_FOLDERS_QUERY>['learningFolders']
export type LearningDemo = LearningFolder[number]['demos'][number]

interface Props {
  demo: LearningDemo
}

export function LearningDemoCard({ demo }: Props) {
  const translations = useTranslate({
    openDemo: 'learning.open-demo',
    closeDemo: 'learning.close-demo',
    completed: 'learning.completed',
    markCompleted: 'learning.mark-completed',
    markNotCompleted: 'learning.mark-not-completed',
    new: 'learning.new',
  })

  const [open, setOpen] = useState(false)

  useDemoViewTracking(demo.id, open)

  const [setCompleted, { loading: saving }] = useMutation(
    SET_LEARNING_DEMO_COMPLETED_MUTATION,
    { refetchQueries: ['LearningFolders'] }
  )

  const completed = demo.progress?.status === 'COMPLETED'
  const isNew =
    !!demo.publishedAt &&
    differenceInDays(new Date(), parseISO(demo.publishedAt)) <= NEW_DEMO_DAYS

  return (
    <li className="border-t border-stone-200 px-5 py-4 first:border-t-0 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-medium text-stone-900">{demo.title}</h4>

            {completed && (
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-900">
                <FAIcon icon="circle-check" />
                {translations.completed}
              </span>
            )}

            {isNew && !completed && (
              <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
                {translations.new}
              </span>
            )}
          </div>

          {!!demo.description && (
            <p className="max-w-prose text-sm text-stone-600">
              {demo.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {open && (
            <Button
              variant="outline"
              size="sm"
              disabled={saving}
              onClick={() =>
                void setCompleted({
                  variables: { demo: demo.id, completed: !completed },
                })
              }
            >
              <FAIcon icon={completed ? 'xmark' : 'check'} />
              {completed
                ? translations.markNotCompleted
                : translations.markCompleted}
            </Button>
          )}

          <Button size="sm" onClick={() => setOpen(value => !value)}>
            <FAIcon
              icon={open ? 'chevron-up' : 'circle-play'}
              type={open ? 'regular' : 'solid'}
            />
            {open ? translations.closeDemo : translations.openDemo}
          </Button>
        </div>
      </div>

      {open && (
        <div className="mt-4">
          <StorylaneEmbed
            demoId={demo.demoId}
            title={demo.title}
            aspectRatioPercent={demo.aspectRatioPercent ?? 72.23}
            chromeHeightPx={demo.chromeHeightPx ?? 25}
          />
        </div>
      )}
    </li>
  )
}
